import React from "react";
import { useTranslation } from "react-i18next";
import {
	CheckboxListSection,
	Section,
	toggleInGroup,
} from "@/components/Shared/Filters/filterControls";
import Checkbox from "../../../components/Core/Checkbox/Checkbox";
import { CustomerFilterDefinition } from "../../../models/CustomerFilter";
import { NO_SUBSIDIARY } from "../../../models/Subsidiary";
import { CustomerFilterOptions } from "./useCustomerFilterData";

interface Props {
	definition: CustomerFilterDefinition;
	onChange: (def: CustomerFilterDefinition) => void;
	options: CustomerFilterOptions;
}

/**
 * The customer set of filter controls (status, signup, org units, pool,
 * contract). Reused by the customer-list filter drawer and the org-defined
 * customer filters settings editor.
 */
const CustomerFilterBuilder: React.FC<Props> = ({ definition, onChange, options }) => {
	const { t } = useTranslation();

	return (
		<>
			<Section title={t("STATUS")} count={definition.status ? 1 : 0}>
				<Checkbox
					customStyle
					labelClassName="text-base"
					label={t("ACTIVE")}
					isChecked={definition.status === "active"}
					onChange={() =>
						onChange({
							...definition,
							status: definition.status === "active" ? undefined : "active",
						})
					}
				/>
				<Checkbox
					customStyle
					labelClassName="text-base"
					label={t("INACTIVE")}
					isChecked={definition.status === "inactive"}
					onChange={() =>
						onChange({
							...definition,
							status: definition.status === "inactive" ? undefined : "inactive",
						})
					}
				/>
			</Section>

			<Section
				title={t("customer.isSignupPlural" as any)}
				divider
				count={definition.isSignup ? 1 : 0}
			>
				<Checkbox
					customStyle
					labelClassName="text-base"
					label={t("customer.isSignupPlural" as any)}
					isChecked={!!definition.isSignup}
					onChange={() =>
						onChange({ ...definition, isSignup: definition.isSignup ? undefined : true })
					}
				/>
			</Section>

			<CheckboxListSection
				title={t("POOL" as any)}
				divider
				options={options.pools.map((p) => ({ value: p.id, label: p.name }))}
				selected={definition.pools}
				onToggle={(value) => onChange(toggleInGroup(definition, "pools", value))}
			/>

			<CheckboxListSection
				title={t("CONTRACT" as any)}
				divider
				options={options.contracts.map((c) => ({ value: c.id, label: c.name }))}
				selected={definition.contractIds}
				onToggle={(value) => onChange(toggleInGroup(definition, "contractIds", value))}
			/>

			{/* OR within the group; NO_SUBSIDIARY matches "organisation directly". */}
			{options.subsidiaries.length > 0 && (
				<CheckboxListSection
					title={t("SUBSIDIARY")}
					divider
					options={[
						{ value: NO_SUBSIDIARY, label: t("NO_SUBSIDIARY") },
						...options.subsidiaries.map((s) => ({ value: s.id, label: s.name })),
					]}
					selected={definition.subsidiaryIds}
					onToggle={(value) => onChange(toggleInGroup(definition, "subsidiaryIds", value))}
				/>
			)}

			<CheckboxListSection
				title={t("Taggar" as any)}
				divider
				options={options.tags.map((tag) => ({ value: tag.id, label: tag.name }))}
				selected={definition.tags}
				onToggle={(value) => onChange(toggleInGroup(definition, "tags", value))}
			/>
		</>
	);
};

export default CustomerFilterBuilder;
